import { useMutation } from "@apollo/client";
import type {
  CreateOfferingInput as CreateOfferingInputType,
  CreateOfferingPayload,
  OfferingInput,
} from "@casey/db-types";
import { CREATE_OFFERING, GET_ORGANIZATION_OFFERINGS } from "../gql";

interface CreateOfferingInput {
  name: string;
  description?: string;
  context?: any;
  organizationId: string;
  createdByUserId: string;
}

interface CreateOfferingVariables {
  input: CreateOfferingInputType;
}

interface CreateOfferingResponse {
  createOffering: CreateOfferingPayload;
}

export function useCreateOffering() {
  const [createOfferingMutation, { data, loading, error }] = useMutation<
    CreateOfferingResponse,
    CreateOfferingVariables
  >(CREATE_OFFERING, {
    // Automatically refetch team offerings after creation
    refetchQueries: [{ query: GET_ORGANIZATION_OFFERINGS }],
    // Update the cache optimistically
    update: (cache, { data }) => {
      if (data?.createOffering?.offering) {
        // Cache update logic can be added here if needed
      }
    },
  });

  const createOffering = async (offeringData: CreateOfferingInput) => {
    try {
      const offeringInput: OfferingInput = {
        name: offeringData.name,
        description: offeringData.description,
        context: offeringData.context
          ? JSON.stringify(offeringData.context)
          : null,
        organizationId: offeringData.organizationId,
        createdByUserId: offeringData.createdByUserId,
      };

      const result = await createOfferingMutation({
        variables: {
          input: {
            offering: offeringInput,
          },
        },
      });

      const createdOffering = result.data?.createOffering?.offering;
      return createdOffering;
    } catch (err) {
      console.error("Error creating offering:", err);
      throw err;
    }
  };

  return {
    createOffering,
    data: data?.createOffering?.offering,
    loading,
    error,
  };
}

export type { CreateOfferingInput };
